// Automated Playoff bracket construction (Phase 2, structural half).
//
// Given the pools, we build a single-elimination bracket whose participants are
// the pool qualifiers (1st + 2nd of every pool). When the advancing field is not
// a power of two, the lowest seeds contest a play-in pre-round (R0) and the top
// seeds receive byes straight into round 1.
//
// Seeding is structural (pool-position based): all 1st-place slots are seeded
// ahead of all 2nd-place slots, in pool order. The slots themselves are filled
// with real player IDs at runtime as pools complete, so the bracket renders and
// reacts in real time. (A statistical cross-group re-seed could refine which
// runner-up earns a bye, but a fixed structure keeps the live wiring stable.)

import type { BracketNode, ParticipantRef } from './types';
import { DEFAULT_BRACKET_BEST_OF } from './types';
import { nodeId, slotId } from './ids';

export interface BracketPoolRef {
  poolId: string;
  label: string;
}

export interface BracketPlan {
  advancing: number; // N = 2 * numGroups
  mainBracketSize: number; // P = largest power of two <= N
  byes: number; // 2P - N
  playInMatches: number; // N - P
  playInPlayers: number; // 2 * (N - P)
  rounds: number; // K = log2(P) main rounds
}

export interface BracketBuildResult {
  nodes: BracketNode[];
  order: string[]; // node ids, play-in round first then R1..final
  plan: BracketPlan;
}

/** Largest power of two that is <= n (n >= 1). */
export function largestPowerOfTwoAtMost(n: number): number {
  let p = 1;
  while (p * 2 <= n) p *= 2;
  return p;
}

/**
 * Standard tournament seeding order for a power-of-two bracket. Returns the seed
 * numbers in match order, e.g. size 8 -> [1,8,4,5,2,7,3,6] so consecutive pairs
 * are the round-1 match-ups (1v8, 4v5, 2v7, 3v6).
 */
export function standardSeedOrder(size: number): number[] {
  let seeds = [1, 2];
  while (seeds.length < size) {
    const sum = seeds.length * 2 + 1;
    const next: number[] = [];
    for (const seed of seeds) {
      next.push(seed);
      next.push(sum - seed);
    }
    seeds = next;
  }
  return seeds;
}

export function planBracket(numGroups: number): BracketPlan {
  const advancing = numGroups * 2;
  const mainBracketSize = largestPowerOfTwoAtMost(advancing);
  const playInMatches = advancing - mainBracketSize;
  const byes = 2 * mainBracketSize - advancing;
  return {
    advancing,
    mainBracketSize,
    byes,
    playInMatches,
    playInPlayers: playInMatches * 2,
    rounds: Math.log2(mainBracketSize),
  };
}

function roundLabel(round: number, totalRounds: number, matchIndex: number): string {
  const fromFinal = totalRounds - round;
  if (fromFinal === 0) return 'Final';
  if (fromFinal === 1) return `Semifinal ${matchIndex}`;
  if (fromFinal === 2) return `Quarterfinal ${matchIndex}`;
  const size = 2 ** (fromFinal + 1);
  return `Round of ${size} — Match ${matchIndex}`;
}

function makeNode(
  round: number,
  matchIndex: number,
  a: ParticipantRef,
  b: ParticipantRef,
  label: string,
  bestOf: number,
): BracketNode {
  return {
    id: nodeId(round, matchIndex),
    round,
    matchIndex,
    a,
    b,
    playerA: null,
    playerB: null,
    bestOf,
    games: [],
    events: [],
    status: 'PENDING',
    winner: null,
    nextNodeId: null,
    nextSlot: null,
    label,
    refereeId: null,
  };
}

/**
 * Builds the full bracket (play-in pre-round + main rounds) for the pools.
 *
 * `bracketBestOf` sets the series length for every match; `finalBestOf` (default
 * `bracketBestOf`) overrides it for the two final-round games — the Final and the
 * third-place playoff — so a deep field can play shorter earlier rounds while
 * still contesting a longer decider.
 */
export function buildBracket(
  pools: readonly BracketPoolRef[],
  bracketBestOf: number = DEFAULT_BRACKET_BEST_OF,
  finalBestOf: number = bracketBestOf,
): BracketBuildResult {
  const plan = planBracket(pools.length);
  const { advancing: N, mainBracketSize: P, byes, rounds } = plan;

  // The Final and the third-place playoff both live in the deepest round
  // (`rounds`); every earlier round uses the general bracket series length.
  const bestOfForRound = (round: number) =>
    round === rounds ? finalBestOf : bracketBestOf;

  // Seed -> slot id. Seed numbers are 1-based: all 1st-place slots first
  // (in pool order), then all 2nd-place slots.
  const seedSlot: string[] = [];
  for (const pool of pools) seedSlot.push(slotId(pool.label, 1));
  for (const pool of pools) seedSlot.push(slotId(pool.label, 2));
  const slotForSeed = (seed: number) => seedSlot[seed - 1];

  const nodes: BracketNode[] = [];
  const order: string[] = [];
  const byId = new Map<string, BracketNode>();
  const push = (node: BracketNode) => {
    nodes.push(node);
    order.push(node.id);
    byId.set(node.id, node);
  };
  const link = (sourceId: string, targetId: string, side: 'a' | 'b') => {
    const source = byId.get(sourceId);
    if (source) {
      source.nextNodeId = targetId;
      source.nextSlot = side;
    }
  };

  // Participant entering main round 1 at each bracket-seed position (1..P):
  // either a direct pool slot (bye) or the winner of a play-in node.
  const seatParticipant = new Array<ParticipantRef>(P + 1);

  // Top `byes` seeds enter directly.
  for (let seed = 1; seed <= byes; seed++) {
    seatParticipant[seed] = { kind: 'slot', slotId: slotForSeed(seed) };
  }

  // Play-in pre-round (R0): bracket seats byes+1..P are won here. Each pairs a
  // high seed (byes+j) against a low seed (N-j+1).
  for (let seat = byes + 1; seat <= P; seat++) {
    const j = seat - byes; // 1..playInMatches
    const highSeed = seat; // byes + j
    const lowSeed = N - j + 1;
    const node = makeNode(
      0,
      j,
      { kind: 'slot', slotId: slotForSeed(highSeed) },
      { kind: 'slot', slotId: slotForSeed(lowSeed) },
      `Play-in ${j}`,
      bestOfForRound(0),
    );
    push(node);
    seatParticipant[seat] = { kind: 'winner', nodeId: node.id };
  }

  // Main round 1, positioned via standard seeding.
  const seatOrder = standardSeedOrder(P);
  let previousRoundIds: string[] = [];
  const round1Matches = P / 2;
  for (let m = 1; m <= round1Matches; m++) {
    const seatA = seatOrder[(m - 1) * 2];
    const seatB = seatOrder[(m - 1) * 2 + 1];
    const a = seatParticipant[seatA];
    const b = seatParticipant[seatB];
    const node = makeNode(1, m, a, b, roundLabel(1, rounds, m), bestOfForRound(1));
    push(node);
    previousRoundIds.push(node.id);
    if (a.kind === 'winner') link(a.nodeId, node.id, 'a');
    if (b.kind === 'winner') link(b.nodeId, node.id, 'b');
  }

  // Subsequent main rounds: pair winners of the previous round.
  for (let r = 2; r <= rounds; r++) {
    const matches = P / 2 ** r;
    const currentIds: string[] = [];
    for (let m = 1; m <= matches; m++) {
      const sourceA = previousRoundIds[(m - 1) * 2];
      const sourceB = previousRoundIds[(m - 1) * 2 + 1];
      const node = makeNode(
        r,
        m,
        { kind: 'winner', nodeId: sourceA },
        { kind: 'winner', nodeId: sourceB },
        roundLabel(r, rounds, m),
        bestOfForRound(r),
      );
      push(node);
      currentIds.push(node.id);
      link(sourceA, node.id, 'a');
      link(sourceB, node.id, 'b');
    }
    previousRoundIds = currentIds;
  }

  // Third-place playoff (bronze final): the two semifinal losers meet. Only
  // exists when the bracket actually has semifinals (rounds >= 2). It sits at
  // the final's round as match 2, advances to nobody, and is fed by `loser`
  // refs so it wires up live as the semifinals resolve.
  if (rounds >= 2) {
    const bronze = makeNode(
      rounds,
      2,
      { kind: 'loser', nodeId: nodeId(rounds - 1, 1) },
      { kind: 'loser', nodeId: nodeId(rounds - 1, 2) },
      'Third-place playoff',
      bestOfForRound(rounds),
    );
    push(bronze);
  }

  return { nodes, order, plan };
}
